/**
 * AI generation panel — multi-country bulk edit creative step.
 */

import { Loader2, Sparkles } from 'lucide-react';

import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';
import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';
import { ModalSection } from '@/features/smswiser-campaigns/components/ScaleDropModal/shared';

import type { useMultiCountryCreative } from '../hooks/useMultiCountryCreative';
import { CountryResultsPanel } from './CountryResultsPanel';
import { MultiCountryAiGenerationRows } from './MultiCountryAiGenerationRows';

type Creative = ReturnType<typeof useMultiCountryCreative>;

interface Props {
  creative: Creative;
  dropLabels?: DropLabel[];
  dropLabelsByCountry?: Record<string, DropLabel[]>;
  showAi: boolean;
  showAiSenders: boolean;
}

export function AiGenerationPanel({ creative, dropLabels, dropLabelsByCountry, showAi, showAiSenders }: Props) {
  const { error: toastError } = useToast();
  if (!showAi && !showAiSenders) return null;
  const hasResults = Object.keys(creative.resultsByCountry).length > 0;

  return (
    <ModalSection title="AI Generation" icon={<Sparkles className="h-3.5 w-3.5" />}>
      <div className="space-y-3">
        <MultiCountryAiGenerationRows
          creative={creative}
          dropLabels={dropLabels}
          showAi={showAi}
          showAiSenders={showAiSenders}
          onTextGenError={(err: unknown) =>
            toastError(
              'Text generation failed',
              getApiErrorMessage(err, 'Request failed or timed out. Try again or reduce drops.'),
            )
          }
          onSenderGenError={(err: unknown) =>
            toastError(
              'Sender generation failed',
              getApiErrorMessage(err, 'Request failed or timed out. Try again or reduce drops.'),
            )
          }
        />

        {creative.isGenerating && creative.progress && (
          <div className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-primary">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {creative.progress.current >= creative.progress.total
              ? `Finishing ${creative.progress.current}/${creative.progress.total}…`
              : `API calls ${creative.progress.current}/${creative.progress.total} done${
                  creative.progress.currentCountry ? ` · active: ${creative.progress.currentCountry}` : ''
                }`}
          </div>
        )}

        {hasResults && (
          <CountryResultsPanel
            countryKeys={creative.countryKeys}
            resultsByCountry={creative.resultsByCountry}
            dropLabelsByCountry={dropLabelsByCountry}
            onTranslateCountry={
              showAi
                ? (ck) =>
                    void creative.translateCountryTexts(ck).catch((err: unknown) => {
                      toastError(
                        'Translation failed',
                        getApiErrorMessage(err, 'Request failed or timed out. Try again.'),
                      );
                    })
                : undefined
            }
            translatingCountryKey={creative.translatingCountryKey}
            enableTextPicks={showAi}
            textPicks={creative.textPicks}
            toggleAiTextPick={creative.toggleAiTextPick}
            setAllAiTextPicksForBatch={creative.setAllAiTextPicksForBatch}
            regenerateUnselectedInBatch={(ck, bi) =>
              void creative.regenerateUnselectedInBatch(ck, bi).catch((err: unknown) => {
                toastError(
                  'Regenerate failed',
                  getApiErrorMessage(err, 'Request failed or timed out. Try again.'),
                );
              })
            }
            regenTarget={creative.regenTarget}
            isRegeneratingTexts={creative.isRegeneratingTexts}
            disableTextPickInteractions={creative.isGenerating}
            translateMissingInBatch={
              showAi
                ? (ck, bi) =>
                    void creative.translateMissingInBatch(ck, bi).catch((err: unknown) => {
                      toastError(
                        'Translation failed',
                        getApiErrorMessage(err, 'Request failed or timed out. Try again.'),
                      );
                    })
                : undefined
            }
            translatingBatch={creative.translatingBatch}
          />
        )}
      </div>
    </ModalSection>
  );
}
